import { FunnelSimple } from "@phosphor-icons/react"
import type { ColDef, ColGroupDef, GridState } from "ag-grid-community"
import { AnimatePresence } from "motion/react"
import { useCallback, useMemo, type ReactNode } from "react"

import { IconButton } from "~/components/design-system/components"
import { DisplayOptionsPopover } from "~/components/planning/DisplayOptionsPopover"
import FilterPopover from "~/components/planning/FilterPopover"
import { FilterRowAnimated } from "~/components/planning/ViewConfiguration"
import Select from "~/components/reusable/Select"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useFilters } from "~/hooks/useFilters"
import {
  FilterFieldsOverrideContext,
  type FilterFieldsOverride,
} from "~/hooks/useFilterFieldsOverride"
import { useAllocationAccess } from "~/hooks/useAllocationAccess"
import type { SavedTableView } from "~/server/api/routers/tableView"
import { useAllocationStoreMap } from "~/hooks/useAllocationStoreMap"
import { useAllocationWarehouseMap } from "~/hooks/useAllocationWarehouseMap"
import { applyFilter, applyGatedFilters, type Filter } from "~/utils/filter"
import { distinctValues } from "~/utils/tsUtils"
import { DEFAULT_LINE_FILTERS, LINE_FILTER_FIELDS } from "./replenishment/lineFilterFields"
import { useLineExtraDataFields } from "./replenishment/useLineExtraData"
import { useAllocationPackages } from "./useAllocationData"
import { useAllocationView } from "./AllocationPlanningContext"
import { SavedViewSwitcher, type SavedViewSnapshot } from "./SavedViewSwitcher"

// The filter fields the reports read. Keep in step with the server's store and
// SKU scope columns (reportScope in lineFilters.ts); the remaining line fields
// only narrow the Replenishment lines.
const SCOPE_FIELD_KEYS = new Set([
  "storeName",
  "market",
  "warehouseName",
  "packageName",
  "productName",
  "variantName",
  "size",
  "color",
])

export const SCOPE_FILTER_FIELDS = LINE_FILTER_FIELDS.filter((field) =>
  SCOPE_FIELD_KEYS.has(field.value),
)

export const isScopeFilter = (filter: Filter) => SCOPE_FIELD_KEYS.has(filter.field)

export function filterRowsById<T extends { id: string }>(
  rows: T[],
  filters: Filter[],
  fieldType: (field: string) => string | undefined,
): T[] {
  if (filters.length === 0) return rows
  const perRow = filters.map((filter) => ({ ...filter, by: "id" }))
  return applyGatedFilters(rows, perRow, (candidates, conjunction) =>
    conjunction.reduce<T[]>(
      (acc, filter) => applyFilter(acc as never, filter, conjunction, fieldType) as unknown as T[],
      candidates,
    ),
  )
}

/** The current page's filters, as the page sends them to the server. Each
 * allocation page keeps its own; team locked filters are merged in here once
 * they exist, so they cover every page. */
export function useAllocationFilters() {
  const { filters, setFilters } = useFilters()
  const serverFilters = useMemo(
    () =>
      filters.map(({ field, operator, value, values, gate, groupId }) => ({
        field,
        operator,
        value,
        values,
        gate,
        groupId,
      })),
    [filters],
  )
  const scopeFilters = useMemo(
    () => serverFilters.filter((filter) => SCOPE_FIELD_KEYS.has(filter.field)),
    [serverFilters],
  )
  return { filters, setFilters, serverFilters, scopeFilters }
}

/** Option lists for the scope fields every page can fill without loading lines. */
export function useScopeFilterOptions(): Record<string, string[]> {
  const { data: storeMap } = useAllocationStoreMap()
  const { data: warehouseMap } = useAllocationWarehouseMap()
  const { packages } = useAllocationPackages()
  return useMemo(() => {
    const stores = Object.values(storeMap ?? {})
    return {
      storeName: distinctValues(stores.map((store) => store.name)),
      market: distinctValues(stores.map((store) => store.country)),
      warehouseName: distinctValues(Object.values(warehouseMap ?? {})),
      packageName: distinctValues(packages.map((pkg) => pkg.name)),
    }
  }, [storeMap, warehouseMap, packages])
}

export function GroupByDisplayOption<K extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { key: K; label: string }[]
  value: K
  onChange: (key: K) => void
}) {
  return (
    <div className="flex flex-row items-center justify-between gap-2">
      <span className="label-base">Group by</span>
      <Select
        className="min-w-37.5"
        value={options.find((option) => option.key === value)}
        onChange={(option) => {
          if (option) onChange(option.key)
        }}
        options={options}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.key}
        isSearchable={false}
        size="sm"
      />
    </div>
  )
}

export function rowGroupHeaderName<T>(
  columnDefs: readonly (ColDef<T> | ColGroupDef<T>)[],
): string | undefined {
  for (const def of columnDefs) {
    const name =
      "children" in def ? rowGroupHeaderName(def.children) : def.rowGroup && def.headerName
    if (name) return name
  }
  return undefined
}

/** Report toolbar in the Replenishment layout: saved views and the page's own
 * controls on the left, filter and display options on the right, filter pills
 * below. The filter popover offers only the scope fields; a view can still
 * bring other filters, and the ones reports ignore are counted. */
export function AllocationReportToolbar({
  tableId,
  views,
  children,
  trailing,
  displayOptions,
}: {
  tableId: string
  views: ReportViewBinding
  children?: ReactNode
  trailing?: ReactNode
  displayOptions?: ReactNode
}) {
  const { filters, setFilters } = useAllocationFilters()
  const showViews = useAllocationAccess().level === "full"
  const options = useScopeFilterOptions()
  const extraData = useLineExtraDataFields()
  const allFields = useMemo<FilterFieldsOverride>(
    () => ({
      fields: extraData.filterFields,
      optionsByField: { ...options, ...extraData.filterOptions },
    }),
    [extraData, options],
  )
  const scopeFields = useMemo<FilterFieldsOverride>(
    () => ({ fields: SCOPE_FILTER_FIELDS, optionsByField: options }),
    [options],
  )
  const ignoredCount = filters.filter((filter) => !isScopeFilter(filter)).length

  return (
    <FilterFieldsOverrideContext.Provider value={allFields}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        {showViews && (
          <>
            <AllocationViewSwitcher binding={views} />
            <span className="mx-1 h-4.5 shrink-0 border-l border-base" />
          </>
        )}
        {children}
        <div className="flex-1" />
        {ignoredCount > 0 && (
          <Tooltip title="Filters on line figures, such as suggested qty, only narrow the Replenishment lines">
            <span className="paragraph-xs text-fg-muted">
              {ignoredCount} {ignoredCount === 1 ? "filter doesn't" : "filters don't"} apply to this
              report
            </span>
          </Tooltip>
        )}
        {trailing}
        <FilterFieldsOverrideContext.Provider value={scopeFields}>
          <FilterPopover
            align="end"
            shortcutEnabled
            customTrigger={
              <IconButton variant="neutral" size="sm" aria-label="Add filter">
                <FunnelSimple />
              </IconButton>
            }
          />
        </FilterFieldsOverrideContext.Provider>
        <DisplayOptionsPopover tableId={tableId} extraOptions={displayOptions} />
      </div>
      <AnimatePresence initial={false}>
        {filters.length > 0 && (
          <FilterRowAnimated
            filters={filters}
            supportsViews={false}
            isCurrentViewChanged={false}
            hasCurrentView={false}
            showClearView={false}
            backToDefault={() => void setFilters(DEFAULT_LINE_FILTERS)}
          />
        )}
      </AnimatePresence>
    </FilterFieldsOverrideContext.Provider>
  )
}

/** How a report table takes part in saved views: its own page id, what a view
 * saves besides the filters, and how the table takes a view back. */
export type ReportViewBinding = {
  page: string
  groupBy: string
  getGridState: () => GridState | undefined
  /** A view was applied, or cleared (null): set the grouping and the grid layout. */
  onApply: (view: SavedTableView | null) => void
}

/** A saved view's grouping when the table offers it, else the table's first option. */
export function savedViewGroupBy<K extends string>(
  view: SavedTableView | null,
  options: readonly { key: K }[],
): K {
  const saved = view?.aggregationMethods[0]
  return options.find((option) => option.key === saved)?.key ?? options[0]!.key
}

/** The saved-view switcher for the report tables, each with its own views. */
export function AllocationViewSwitcher({ binding }: { binding: ReportViewBinding }) {
  const { page, groupBy, getGridState, onApply } = binding
  const { activeView, setActiveView } = useAllocationView(page)
  const { filters, setFilters } = useAllocationFilters()
  const applyView = useCallback(
    (view: SavedTableView) => {
      setActiveView(view)
      void setFilters(view.filters)
      onApply(view)
    },
    [setActiveView, setFilters, onApply],
  )
  const clearView = useCallback(() => {
    setActiveView(null)
    void setFilters(DEFAULT_LINE_FILTERS)
    onApply(null)
  }, [setActiveView, setFilters, onApply])
  const getSnapshot = useCallback(
    (): SavedViewSnapshot => ({ filters, groupBy, gridState: getGridState() }),
    [filters, groupBy, getGridState],
  )
  return (
    <SavedViewSwitcher
      page={page}
      activeView={activeView}
      onApply={applyView}
      onClear={clearView}
      getSnapshot={getSnapshot}
    />
  )
}
